import { useCallback, useMemo, useState } from 'react';

import GeneratikTableDefaultDisplayFooter from './GeneratikTableDefaultDisplayFooter';
import GeneratikTableDialogDisplayFooter from './GeneratikTableDialogDisplayFooter';
import { GeneratikTableFooterProps } from './GeneratikTableFooter.types';

const GeneratikTableFooter = ({
  page,
  pageSize,
  rowCount,
  pageSizeOptions = [10, 25, 50, 100],
  onPageChange,
  onPageSizeChange,
  pageTotal,
  pageSelected,
  spacesTotal,
  productionTotal,
  grandTotal,
  currency = '€',
  hidePagination,
  selectedRowsCount,
  modalActions,
  stackedModalFooter,
}: GeneratikTableFooterProps) => {
  const [pageSizeAnchor, setPageSizeAnchor] = useState<null | HTMLElement>(null);

  const totalPages = useMemo(() => Math.ceil(rowCount / pageSize), [rowCount, pageSize]);

  const startRow = useMemo(() => page * pageSize + 1, [page, pageSize]);
  const endRow = useMemo(
    () => Math.min((page + 1) * pageSize, rowCount),
    [page, pageSize, rowCount],
  );

  const canGoPrevious = page > 0;
  const canGoNext = page < totalPages - 1;

  const handlePreviousPage = useCallback(() => {
    if (canGoPrevious) {
      onPageChange?.(page - 1);
    }
  }, [canGoPrevious, onPageChange, page]);

  const handleNextPage = useCallback(() => {
    if (canGoNext) {
      onPageChange?.(page + 1);
    }
  }, [canGoNext, onPageChange, page]);

  const handlePageSizeClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setPageSizeAnchor(event.currentTarget);
  }, []);

  const handlePageSizeClose = useCallback(() => {
    setPageSizeAnchor(null);
  }, []);

  const handlePageSizeSelect = useCallback(
    (size: number) => {
      onPageSizeChange?.(size);
      handlePageSizeClose();
    },
    [onPageSizeChange, handlePageSizeClose],
  );

  const formatTotal = (value: number | string | undefined): string => {
    if (value === undefined) return '';
    if (typeof value === 'string') return value;
    return `${new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(value)} ${currency}`;
  };

  return modalActions ? (
    <GeneratikTableDialogDisplayFooter
      pageSize={pageSize}
      rowCount={rowCount}
      startRow={startRow}
      endRow={endRow}
      pageSizeOptions={pageSizeOptions}
      pageTotal={pageTotal}
      pageSelected={pageSelected}
      hidePagination={hidePagination}
      selectedRowsCount={selectedRowsCount}
      pageSizeAnchor={pageSizeAnchor}
      handlePageSizeSelect={handlePageSizeSelect}
      formatTotal={formatTotal}
      handlePageSizeClick={handlePageSizeClick}
      handlePageSizeClose={handlePageSizeClose}
      handlePreviousPage={handlePreviousPage}
      handleNextPage={handleNextPage}
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext}
      modalActions={modalActions}
      stackedModalFooter={stackedModalFooter}
    />
  ) : (
    <GeneratikTableDefaultDisplayFooter
      pageSize={pageSize}
      rowCount={rowCount}
      startRow={startRow}
      endRow={endRow}
      pageSizeOptions={pageSizeOptions}
      pageTotal={pageTotal}
      pageSelected={pageSelected}
      spacesTotal={spacesTotal}
      productionTotal={productionTotal}
      grandTotal={grandTotal}
      hidePagination={hidePagination}
      selectedRowsCount={selectedRowsCount}
      pageSizeAnchor={pageSizeAnchor}
      handlePageSizeSelect={handlePageSizeSelect}
      formatTotal={formatTotal}
      handlePageSizeClick={handlePageSizeClick}
      handlePageSizeClose={handlePageSizeClose}
      handlePreviousPage={handlePreviousPage}
      handleNextPage={handleNextPage}
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext}
    />
  );
};

export default GeneratikTableFooter;
